import React from 'react';
import PropTypes from 'prop-types';

// Icon components for different button types
const DownloadIcon = () => (
  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const CalculateIcon = () => (
  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const ExportIcon = () => (
  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const EditIcon = () => (
  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const BackIcon = () => (
  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const PlusIcon = () => (
  <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
  </svg>
);

const getIcon = (iconType) => {
  switch (iconType) {
    case 'download':
      return <DownloadIcon />;
    case 'calculate':
      return <CalculateIcon />;
    case 'export':
      return <ExportIcon />;
    case 'edit':
      return <EditIcon />;
    case 'back':
      return <BackIcon />;
    case 'plus':
      return <PlusIcon />;
    default:
      return <DownloadIcon />;
  }
};

const SuccessionPageLayout = ({ title, subtitle, actionButtons, children }) => {
  const containerRef = React.useRef(null);
  const [containerHeight, setContainerHeight] = React.useState('100vh');

  // Calculate available height when no title (full viewport mode)
  React.useEffect(() => {
    if (title) return; // Only calculate for no-title mode
    
    function calculateHeight() {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const availableHeight = window.innerHeight - rect.top;
        setContainerHeight(`${availableHeight}px`);
      }
    }
    
    // Initial calculation
    calculateHeight();
    
    // Recalculate on resize
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, [title]);

  const renderActionButtons = () => {
    if (!actionButtons || actionButtons.length === 0) {
      return null;
    }

    return (
      <div className="mt-4 sm:mt-0">
        <div className="flex flex-col sm:flex-row gap-3">
          {actionButtons.map((button, index) => (
            <button
              key={index}
              onClick={button.onClick}
              disabled={button.disabled}
              className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary disabled:opacity-50 disabled:cursor-not-allowed ${
                button.variant === 'primary'
                  ? 'border-transparent bg-succession-primary text-white hover:opacity-80'
                  : button.variant === 'danger'
                  ? 'border-transparent bg-succession-error text-white hover:opacity-80'
                  : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:bg-succession-bg-secondary'
              }`}
            >
              {getIcon(button.icon)}
              {button.text}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // When no title, dynamically calculate height to fill viewport
  const rootClasses = title 
    ? 'bg-succession-bg-secondary flex flex-col min-h-screen'
    : 'bg-succession-bg-secondary flex flex-col overflow-hidden';

  const rootStyle = title ? {} : { height: containerHeight };

  return (
    <div ref={containerRef} className={rootClasses} style={rootStyle}>
      {/* Header - Full Width - Only show if title is provided */}
      {title && (
        <div className="bg-succession-bg-primary shadow-sm border-b border-succession-border w-full flex-shrink-0">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="py-6">          
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-succession-text-primary">
                    {title}
                  </h1>
                  <p className="mt-2 text-sm text-succession-text-secondary">
                    {subtitle}
                  </p>
                </div>
                
                {renderActionButtons()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`w-full ${title ? 'px-4 sm:px-6 lg:px-8 py-8' : 'p-4 flex-1 min-h-0'}`}>
        {children}
      </div>
    </div>
  );
};

SuccessionPageLayout.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  actionButtons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      icon: PropTypes.oneOf(['download', 'calculate', 'export', 'edit', 'back', 'plus']),
      disabled: PropTypes.bool,
      variant: PropTypes.oneOf(['default', 'primary', 'danger'])
    })
  ),
  children: PropTypes.node.isRequired
};

SuccessionPageLayout.defaultProps = {
  actionButtons: []
};

export default SuccessionPageLayout;